/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bidiUtils } from '@ba-ui-toolkit/utils';

const { resolveBaseTextDir } = bidiUtils;

/**
 * TabLabel is an editable span element that supports cancel/revert via the esc key
 */
export default class TabLabel extends Component {
	static propTypes = {
		className: PropTypes.string,
		contentEditable: PropTypes.bool,
		changeHandler: PropTypes.func.isRequired,
		focusOnComplete: PropTypes.func,
		focusHandler: PropTypes.func,
		label: PropTypes.string.isRequired,
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		style: PropTypes.object,
		disableDrag: PropTypes.func,
		enableDrag: PropTypes.func,
		onLabelBlur: PropTypes.func,
	};

	static defaultProps = {
		className: '',
		style: {},
	};

	_onFocus = e => {
		if (this.props.disableDrag) {
			this.props.disableDrag();
		}
		this.labelInitialValue = e.target.textContent;
		this._selectAllTextInTarget(e.target);
		this.props.focusHandler && this.props.focusHandler(true);
	};

	_selectAllTextInTarget = target => {
		if (this._isCreateTextRangeSupported()) {
			const range = document.body.createTextRange();
			range.moveToElementText(target);
			range.select();
		} else if (this._isGetSelectionSupported()) {
			const selection = window.getSelection();
			const newRange = document.createRange();
			newRange.selectNodeContents(target);
			selection.removeAllRanges();
			selection.addRange(newRange);
		}
	};

	_isCreateTextRangeSupported() {
		if (document.body.createTextRange) return true;

		return false;
	}

	_isGetSelectionSupported() {
		if (window.getSelection) return true;

		return false;
	}

	_onBlur = e => {
		const { enableDrag, changeHandler, onLabelBlur } = this.props;

		if (e.target.textContent === '') {
			e.target.textContent = this.labelInitialValue;
		}

		changeHandler(e);
		if (enableDrag) {
			enableDrag();
		}
		onLabelBlur && onLabelBlur();
	};

	_onKeyDown = e => {
		e.stopPropagation();
		switch (e.keyCode) {
			// esc key - revert edit
			case 27:
				e.target.textContent = this.labelInitialValue;
				this.props.focusOnComplete && this.props.focusOnComplete();
				this.props.onLabelBlur();
				break;
			// enter - done focus on parent tab
			case 13:
				this.props.focusOnComplete && this.props.focusOnComplete();
				this.props.onLabelBlur();
				break;
			default:
		}
	};

	_onClick = () => {
		this.focusOnLabel();
	};

	focusOnLabel() {
		if (this.props.contentEditable) {
			this.labelInput.focus();
		}
	}

	render() {
		const { className, contentEditable, label, style, textDir } = this.props;
		const defaultProps = {
			className,
			tabIndex: -1,
			ref: input => {
				this.labelInput = input;
			},
			style,
		};

		if (!contentEditable) {
			return (
				<span dir={resolveBaseTextDir(label, textDir, false)} {...defaultProps}>
					{label}
				</span>
			);
		}

		return (
			<span
				{...defaultProps}
				contentEditable={contentEditable}
				onBlur={this._onBlur}
				onFocus={this._onFocus}
				onKeyDown={this._onKeyDown}
				onClick={this._onClick}
				suppressContentEditableWarning="true"
				dir={resolveBaseTextDir(label, textDir, false)}
			>
				{label}
			</span>
		);
	}
}
